import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Edit2, Trash2, CheckCircle2, XCircle, Loader2, SkipForward } from 'lucide-react';

type ExecutionStatus = 'idle' | 'running' | 'success' | 'error' | 'skipped';

interface LLMStepNodeData {
  stepName: string;
  prompt: string;
  provider: string;
  model: string;
  costEstimate: string;
  isVisionStep?: boolean;
  executionStatus?: ExecutionStatus;
  onEdit?: () => void;
  onDelete?: () => void;
}

const executionBorderColor: Record<ExecutionStatus, string> = {
  idle: '',
  running: 'border-blue-500 shadow-blue-300 shadow-lg',
  success: 'border-green-500 shadow-green-200',
  error: 'border-red-500 shadow-red-200',
  skipped: 'border-gray-300 opacity-60',
};

function LLMStepNode({ data, selected }: NodeProps<LLMStepNodeData>) {
  const execStatus = data.executionStatus || 'idle';
  const borderClass = execStatus !== 'idle'
    ? executionBorderColor[execStatus]
    : selected ? 'border-blue-500 shadow-blue-200' : 'border-blue-300';

  return (
    <div
      className={`bg-white rounded-lg border-2 shadow-lg min-w-[280px] max-w-[320px] relative ${borderClass} ${
        execStatus === 'running' ? 'animate-pulse' : ''
      }`}
    >
      {/* Execution Status Badge */}
      {execStatus !== 'idle' && (
        <div className="absolute -top-2 -right-2 z-10">
          {execStatus === 'running' && <Loader2 className="w-5 h-5 text-blue-500 animate-spin bg-white rounded-full" />}
          {execStatus === 'success' && <CheckCircle2 className="w-5 h-5 text-green-500 bg-white rounded-full" />}
          {execStatus === 'error' && <XCircle className="w-5 h-5 text-red-500 bg-white rounded-full" />}
          {execStatus === 'skipped' && <SkipForward className="w-5 h-5 text-gray-400 bg-white rounded-full" />}
        </div>
      )}
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 !bg-blue-500"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-2 rounded-t-md flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          <span className="font-semibold text-sm">LLM Step</span>
        </div>
        <div className="flex gap-1 nodrag nopan" style={{ pointerEvents: 'auto' }}>
          {data.onEdit && (
            <button
              type="button"
              className="h-6 w-6 p-1 flex items-center justify-center hover:bg-blue-600/20 rounded transition-colors cursor-pointer nodrag"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                console.log('✏️ Edit clicked!', data.stepName);
                if (data.onEdit) {
                  data.onEdit();
                }
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              title="Edit node"
            >
              <Edit2 className="w-3 h-3 text-white" style={{ pointerEvents: 'none' }} />
            </button>
          )}
          {data.onDelete && (
            <button
              type="button"
              className="h-6 w-6 p-1 flex items-center justify-center hover:bg-red-600/20 rounded transition-colors cursor-pointer nodrag"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                console.log('🗑️ Delete clicked!', data.stepName);
                if (data.onDelete) {
                  data.onDelete();
                }
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              title="Delete node"
            >
              <Trash2 className="w-3 h-3 text-white" style={{ pointerEvents: 'none' }} />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        <div>
          <div className="flex items-center gap-2">
            <div className="font-semibold text-sm text-gray-900">{data.stepName}</div>
            {data.isVisionStep && (
              <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                🖼️ Vision
              </span>
            )}
          </div>
          <div className="text-xs text-gray-600 mt-1 line-clamp-3">
            {data.isVisionStep ? 'Analyzes images with Claude vision API' : data.prompt}
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t">
          <Badge variant="secondary" className="text-xs">
            {data.provider}
          </Badge>
          <span className="text-xs text-gray-500">{data.costEstimate}</span>
        </div>

        <div className="text-xs text-gray-500">{data.model}</div>
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 !bg-blue-500"
      />
    </div>
  );
}

export default memo(LLMStepNode);

